import * as ImagePicker from 'expo-image-picker';
import type { ImagePickerAsset } from 'expo-image-picker';
import { useCallback, useRef, useState } from 'react';
import type { TextInput } from 'react-native';

import type { GiphyGif } from '@/src/features/giphy/giphy-service';
import { focusTextInputAt } from '@/src/features/posts/ProfileMentionInput';
import type { ProfileMention } from '@/src/features/posts/post-service';
import { trackEvent } from '@/src/lib/analytics';

import type { SelectedCommentMedia } from './CommentComposer';
import {
  composerStateForReply,
  type CommentReplyTarget,
} from './comment-threading';

export function useCommentComposerState({
  giphySelectEventName,
  maxVideoDurationMillis,
  maxVideoDurationSeconds,
  mediaPasteEventName,
  mediaSelectEventName,
  showError,
}: {
  giphySelectEventName?: string;
  maxVideoDurationMillis: number;
  maxVideoDurationSeconds: number;
  mediaPasteEventName?: string;
  mediaSelectEventName?: string;
  showError: (error: unknown, fallbackMessage?: string) => void;
}) {
  const inputRef = useRef<TextInput | null>(null);
  const composerFocusedRef = useRef(false);
  const restoreComposerFocusRef = useRef(false);
  const [body, setBody] = useState('');
  const [profileMentions, setProfileMentions] = useState<ProfileMention[]>([]);
  const [uploadMedia, setUploadMedia] = useState<SelectedCommentMedia | null>(
    null,
  );
  const [uploadStatus, setUploadStatus] = useState<string | null>(null);
  const [giphyGif, setGiphyGif] = useState<GiphyGif | null>(null);
  const [giphyVisible, setGiphyVisible] = useState(false);
  const [replyTarget, setReplyTarget] = useState<CommentReplyTarget | null>(
    null,
  );
  const canSubmit = Boolean(body.trim() || uploadMedia || giphyGif);

  const resetComposer = useCallback(() => {
    setBody('');
    setProfileMentions([]);
    setUploadMedia(null);
    setUploadStatus(null);
    setGiphyGif(null);
    setReplyTarget(null);
  }, []);

  const rememberComposerFocusForPicker = useCallback(() => {
    restoreComposerFocusRef.current = composerFocusedRef.current;
  }, []);

  const restoreComposerFocusIfNeeded = useCallback(
    (cursor = body.length, delay = 80) => {
      if (!restoreComposerFocusRef.current) {
        return;
      }

      restoreComposerFocusRef.current = false;
      setTimeout(() => {
        focusTextInputAt(inputRef, cursor);
      }, delay);
    },
    [body.length],
  );

  const handleComposerFocusChange = useCallback((focused: boolean) => {
    composerFocusedRef.current = focused;
  }, []);

  const beginReply = useCallback(
    (
      target: CommentReplyTarget,
      options: {
        beforeFocus?: () => void;
        focusDelay?: number;
      } = {},
    ) => {
      const nextState = composerStateForReply({
        body,
        mentions: profileMentions,
        target,
      });

      setReplyTarget(target);
      setBody(nextState.body);
      setProfileMentions(nextState.mentions);
      options.beforeFocus?.();
      setTimeout(() => {
        focusTextInputAt(inputRef, nextState.cursor);
      }, options.focusDelay ?? 80);
    },
    [body, profileMentions],
  );

  const beginFreshReply = useCallback(
    (target: CommentReplyTarget, focusDelay = 80) => {
      const nextState = composerStateForReply({
        body: '',
        mentions: [],
        target,
      });

      setReplyTarget(target);
      setBody(nextState.body);
      setProfileMentions(nextState.mentions);
      setTimeout(() => {
        focusTextInputAt(inputRef, nextState.cursor);
      }, focusDelay);
    },
    [],
  );

  const attachMedia = useCallback(async (kind: SelectedCommentMedia['kind']) => {
    rememberComposerFocusForPicker();

    const permission = await ImagePicker.requestMediaLibraryPermissionsAsync();

    if (!permission.granted) {
      restoreComposerFocusRef.current = false;
      showError(new Error('Photo library access is required to attach media.'));
      return;
    }

    const result = await ImagePicker.launchImageLibraryAsync({
      allowsEditing: false,
      allowsMultipleSelection: false,
      mediaTypes: kind === 'video' ? ['videos'] : ['images'],
      quality: 0.85,
      videoMaxDuration: maxVideoDurationSeconds,
    });

    if (result.canceled) {
      restoreComposerFocusRef.current = false;
      return;
    }

    const asset = result.assets[0];

    if (!asset) {
      restoreComposerFocusRef.current = false;
      return;
    }

    if (
      asset.type === 'video' &&
      asset.duration != null &&
      asset.duration > maxVideoDurationMillis
    ) {
      restoreComposerFocusRef.current = false;
      showError(new Error('Videos must be 10 minutes or shorter.'));
      return;
    }

    setUploadMedia({
      asset,
      kind,
    });
    setGiphyGif(null);
    restoreComposerFocusIfNeeded();

    if (mediaSelectEventName) {
      trackEvent(mediaSelectEventName, {
        media_kind: kind,
      });
    }
  }, [
    maxVideoDurationMillis,
    maxVideoDurationSeconds,
    mediaSelectEventName,
    rememberComposerFocusForPicker,
    restoreComposerFocusIfNeeded,
    showError,
  ]);

  const attachPastedImage = useCallback(
    (asset: ImagePickerAsset) => {
      setUploadMedia({
        asset,
        kind: 'image',
      });
      setGiphyGif(null);
      setUploadStatus(null);

      if (mediaPasteEventName) {
        trackEvent(mediaPasteEventName, { media_kind: 'image' });
      }
    },
    [mediaPasteEventName],
  );

  const clearMedia = useCallback(() => {
    rememberComposerFocusForPicker();
    setUploadMedia(null);
    setGiphyGif(null);
    setUploadStatus(null);
    restoreComposerFocusIfNeeded();
  }, [rememberComposerFocusForPicker, restoreComposerFocusIfNeeded]);

  const openGiphy = useCallback(() => {
    rememberComposerFocusForPicker();
    setGiphyVisible(true);
  }, [rememberComposerFocusForPicker]);

  const closeGiphy = useCallback(() => {
    setGiphyVisible(false);
    restoreComposerFocusIfNeeded();
  }, [restoreComposerFocusIfNeeded]);

  const selectGiphy = useCallback(
    (gif: GiphyGif) => {
      if (giphySelectEventName) {
        trackEvent(giphySelectEventName);
      }

      setGiphyGif(gif);
      setUploadMedia(null);
      setUploadStatus(null);
      restoreComposerFocusIfNeeded();
    },
    [giphySelectEventName, restoreComposerFocusIfNeeded],
  );

  return {
    attachMedia,
    attachPastedImage,
    beginFreshReply,
    beginReply,
    body,
    canSubmit,
    clearMedia,
    closeGiphy,
    giphyGif,
    giphyVisible,
    handleComposerFocusChange,
    inputRef,
    openGiphy,
    profileMentions,
    replyTarget,
    resetComposer,
    restoreComposerFocusIfNeeded,
    selectGiphy,
    setBody,
    setGiphyGif,
    setGiphyVisible,
    setProfileMentions,
    setReplyTarget,
    setUploadMedia,
    setUploadStatus,
    uploadMedia,
    uploadStatus,
  };
}
